<template>
  <div class="search-background" @click="close">
    <div class="search-modal-wrapper" @click.stop>
      <div class="search-panel-wrapper smooth-shadow">
        <div class="search-input" @click.stop>
          <Icon>search</Icon>
          <input
            ref="searchInput"
            v-model="searchTerm"
            type="text"
            class="ghost"
            placeholder="Search Places, Coordinates, localities..."
          />
          <spinner v-if="isSearching" small />
          <Icon type="button" @click="$emit('clickClose')">clear</Icon>
        </div>
        <ul class="result-tabs">
          <li
            v-for="tab in getTabs"
            :key="tab.id"
            :class="[{ active: tab.isActive }]"
            @click="setActiveTab(tab.id)"
          >
            {{ tab.name }}
          </li>
        </ul>
      </div>
      <div class="suggestions-wrapper" @click.stop>
        <simplebar
          v-if="getResults && getResults.length > 0"
          class="city-list"
          data-auto-hide="true"
        >
          <div
            v-for="(item, index) in getResults"
            :key="`${index}_${item.id}`"
            class="city-item"
            @click="selectItem(item)"
          >
            <div class="flex-col flex-expand">
              <h3>{{ item.name }}</h3>
              <p>{{ item.description }}</p>
            </div>
            <Icon>launch</Icon>
          </div>
        </simplebar>
        <div class="wrapper" v-else-if="isLatLong">
          <div class="latlong-button" @click="setJumpLocation">
            <div class="flex-col flex-expand">
              <h3>Go to location</h3>
              <p>{{ searchTerm }}</p>
            </div>
            <icon>pin_drop</icon>
          </div>
        </div>

        <div class="help-text" v-else>
          <p>
            No results found. Try a different query to search for this category.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { forwardGeocode } from "@/api/mapbox/geocode";
import debounce from "lodash/debounce";

export default {
  props: {
    cities: Array,
    entities: Array,
  },
  components: {},
  data() {
    return {
      searchTerm: "",
      activeTab: "all",
      defaultTabs: {
        all: {
          id: "all",
          name: "All results",
          type: "default",
        },
        place: {
          id: "place",
          name: "Places",
          type: "default",
        },
        poi: {
          id: "poi",
          name: "POIs",
          type: "default",
        },
      },
      entityTabs: null,
      results: null,
      isSearching: false,
      zoomLevels: {
        place: 12,
        poi: 16,
        region: 12,
        locality: 12,
        entities: 16,
        latlong: 16,
      },
    };
  },
  computed: {
    ...mapGetters("Location", ["getFetchingCities"]),
    /**
     * Filters the results based on the tab selected
     */
    getResults() {
      // check if the current tab is the default tab,
      // if it's the default use the results from the mapbox search result
      if (this.defaultTabs[this.activeTab]) {
        const currentTab = this.defaultTabs[this.activeTab];
        // if the tab is of all results, return all the results
        if (this.activeTab === "all") {
          return this.results;
        } else {
          // else filter the results for that tab id
          return this.results.filter((item) =>
            item.place_type ? item.place_type.includes(this.activeTab) : false
          );
        }
      } else if (this.entityTabs[this.activeTab]) {
        const currentTab = this.entityTabs[this.activeTab];
        const { entities } = this.entities.find(
          (item) => item.id === this.activeTab
        );
        const regex = new RegExp(this.searchTerm, "i");
        return entities
          .filter((item) => regex.test(item.name))
          .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
          .map((item) => {
            return {
              description: currentTab.name,
              ...item,
              selection_type: {
                type: "entity",
                mode: "entity",
                entity_id: currentTab.id,
                zoom: this.zoomLevels["entities"],
              },
            };
          });
      }
      return this.results;
    },
    isLatLong() {
      const regex = /^[\+\-]?\d\d?[\.]?\d+\,[\s]?[\+\-]?\d\d?[\.]?\d+/g;
      if (regex.test(this.searchTerm)) {
        return true;
      } else {
        return false;
      }
    },
    /**
     * Returns the list of all the tabs
     */
    getTabs() {
      // create the entities tabs based on the passed entities
      if (this.entities && this.entities.length > 0) {
        this.entityTabs = this.entities.reduce((result, tab) => {
          return Object.assign(result, {
            [tab.id]: {
              name: tab.name,
              id: tab.id,
              type: "entity",
            },
          });
        }, {});
      }

      const tabs = [
        ...Object.keys(this.defaultTabs).map(
          (tab_id) => this.defaultTabs[tab_id]
        ),
        ...(this.entityTabs
          ? Object.keys(this.entityTabs).map(
              (tab_id) => this.entityTabs[tab_id]
            )
          : []),
      ];

      return tabs.map((tab) => {
        return {
          ...tab,
          isActive: this.activeTab === tab.id,
        };
      });
    },
  },
  methods: {
    ...mapActions("Location", ["setActiveCity", "setLocation"]),
    setActiveTab(id) {
      this.activeTab = id;
    },
    selectItem(item) {
      this.saveHistory(item);
      //   this.setActiveCity(item);
      if (this.defaultTabs[this.activeTab]) {
        this.$emit("select", item);
      }
      this.$emit("clickClose");
    },
    closeSearch({ keyCode }) {
      if (keyCode === 27) {
        document.removeEventListener("keyup", this.closeSearch, false);
        this.$emit("clickClose");
      }
    },
    /**
     * Saves the current selected item into the history
     */
    saveHistory(item) {
      const currentHistory = this.getHistory();
      if (!currentHistory) {
        this.setHistory([item]);
      } else {
        this.setHistory([
          item,
          ...currentHistory
            .filter((history) => history.name !== item.name)
            .slice(0, 9),
        ]);
      }
    },
    close() {
      this.$emit("clickClose");
    },
    setJumpLocation() {
      const latlong = this.searchTerm
        .split(" ")
        .map((item) => parseFloat(item));
      this.$emit("select", {
        selection_type: {
          type: "coordinates",
          mode: "coordinated",
          location: latlong,
        },
      });
      this.$emit("clickClose");
    },

    /**
     * Searches the mapbox api for the specified search term
     * if the search term is empty, sets the results as history
     */
    searchMapbox: async function (term) {
      if (!term) {
        this.results = this.getHistory();
        this.isSearching = false;
        return;
      }
      this.isSearching = true;
      const response = await forwardGeocode({ query: term });
      const { status, data } = response;
      if (status === 200) {
        this.results = data.features.map((item) => {
          return {
            ...item,
            name: item.text,
            description: item.place_name,
            selection_type: {
              type: item.place_type[0],
              mode: "mapbox",
              location: item.center,
              zoom: this.zoomLevels[item.place_type[0]] || 12,
            },
          };
        });
      }
      this.isSearching = false;
    },
    /**
     * Fetches the history from local storage
     */
    getHistory() {
      return JSON.parse(localStorage.getItem("searchHistory"));
    },
    /**
     * Sets the history to localstorage
     */
    setHistory(items) {
      localStorage.setItem("searchHistory", JSON.stringify(items));
    },
  },
  watch: {
    searchTerm(value) {
      if (!this.isSearching) {
        this.isSearching = true;
      }
      this.debouncedSearchMapbox(value);
    },
  },
  mounted() {
    this.$refs.searchInput.focus();
    document.addEventListener("keyup", this.closeSearch);
    // load the search history
    this.results = this.getHistory();
  },
  created() {
    // create a debounced version of the mapbox search functionality
    this.debouncedSearchMapbox = debounce(this.searchMapbox, 1000);
  },
};
</script>

<style lang="scss" scoped>
.search-background {
  width: 100%;
  height: 100%;
  position: absolute;
  background: var(--popup-background-color);
  top: 0;
  left: 0;
  z-index: 999;
  display: flex;
  justify-content: center;
  padding-top: 100px;
}
.search-modal-wrapper {
  width: 600px;
  display: flex;
  flex-direction: column;

  .search-panel-wrapper {
    display: flex;
    flex-direction: column;
    background: var(--color-secondary);
    border-radius: 5px 5px 0 0;
    border-bottom: 1px solid var(--border-color);
  }
  .search-input {
    display: flex;
    padding: 10px 10px 10px 20px;
    background: var(--color-secondary);
    // border: 1px solid var(--border-color);
    border-radius: 5px;
    align-items: center;

    // &:focus-within {
    //   border: 1px solid var(--color-accent);
    // }

    input {
      margin: 0;
      background: transparent;
      flex: 1;
      border: 0;
      font-size: 1.2em;
      max-width: 100%;
    }
  }

  .result-tabs {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0;
    padding: 0 15px;
    list-style-type: none;

    li {
      padding: 15px;
      border-bottom: 2px solid transparent;
      transition: 0.3s all ease-in-out;

      &.active {
        border-bottom: 2px solid var(--color-primary);
        color: var(--color-primary);
      }

      &:hover {
        color: var(--color-primary);
        cursor: pointer;
      }
    }
  }

  .suggestions-wrapper {
    display: flex;
    flex-direction: column;
    background: var(--color-default-background);
    max-height: 400px;
    border-radius: 0 0 5px 5px;

    .help-text {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      p {
        text-align: center;
        margin: 50px 20px;
        opacity: 0.7;
      }
    }

    .city-list {
      display: flex;
      flex-direction: column;
      overflow-y: auto;
      background: var(--color-secondary);
    }

    .wrapper {
      background: var(--color-secondary);
    }
    .latlong-button {
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 20px;
      border-radius: 5px;
      background: var(--color-secondary);

      p {
        margin: 0;
        padding-top: 10px;
        opacity: 0.7;
      }

      &:hover {
        background: var(--color-primary-light);
        cursor: pointer;
        color: var(--color-primary);

        h3 {
          color: var(--color-primary);
        }
      }
    }

    .city-item {
      display: flex;
      flex-direction: row;
      padding: 20px;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid var(--border-color);
      background: var(--color-secondary);
      i {
        opacity: 0.5;
      }

      p {
        margin: 0;
        opacity: 0.6;
      }

      &:hover {
        background: var(--color-primary-light);
        cursor: pointer;
        color: var(--color-primary);

        h3 {
          color: var(--color-primary);
        }
      }

      &:last-child {
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        border-bottom: 0;
      }
    }
  }
}
</style>
